import { useEffect, useState, useCallback } from 'react'
import { Badge, getStatusVariant } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { CheckCircle, ChevronLeft, ChevronRight, Filter } from 'lucide-react'
import api from '@/services/api'
import { toast } from 'sonner'

interface Commission {
  id: string
  valorCentavos: number
  status: string
  dataPagamento: string | null
  createdAt: string
  deal: {
    id: string
    nome: string
    valor: string
    etapa: string
    empresa: { razaoSocial: string; nomeFantasia: string | null }
  }
  beneficiario: {
    id: string
    name: string
    email: string
    role: string
  }
}

interface Meta {
  page: number
  limit: number
  totalItems: number
  totalPages: number
}

function formatCurrency(cents: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(cents / 100)
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('pt-BR')
}

interface ComissoesTableProps {
  period?: string
  isAdmin?: boolean
}

export function ComissoesTable({ period, isAdmin }: ComissoesTableProps) {
  const [data, setData] = useState<Commission[]>([])
  const [meta, setMeta] = useState<Meta | null>(null)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<string>('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (period) params.set('month', period)
      if (statusFilter) params.set('status', statusFilter)
      params.set('page', String(page))
      params.set('limit', '20')

      const endpoint = isAdmin ? '/commissions/admin' : '/commissions'
      const res = await api.get(`${endpoint}?${params.toString()}`)
      setData(res.data.data)
      setMeta(res.data.meta)
    } catch {
      toast.error('Erro ao carregar comissões')
    } finally {
      setLoading(false)
    }
  }, [period, statusFilter, page, isAdmin])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handlePay = async (id: string) => {
    try {
      await api.patch(`/commissions/${id}/pay`, {})
      toast.success('Comissão marcada como paga')
      fetchData()
    } catch (err: any) {
      toast.error(err.message || 'Erro ao pagar comissão')
    }
  }

  const statusOptions = [
    { value: '', label: 'Todos' },
    { value: 'PENDENTE', label: 'Pendente' },
    { value: 'PAGO', label: 'Pago' },
    { value: 'ESTORNADO', label: 'Estornado' },
  ]

  return (
    <div className='glass-card'>
      {/* Header com filtros */}
      <div className='flex items-center justify-between p-5 border-b border-[var(--glass-border)]'>
        <h3 className='font-semibold text-[var(--font-color-h3)]' style={{ fontSize: 'var(--font-size-h3)' }}>
          Comissões
        </h3>
        <div className='flex items-center gap-2'>
          <Filter size={14} className='text-[var(--text-muted)]' />
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
            className='bg-[var(--glass-bg)] border border-[var(--glass-border)] text-[var(--text-primary)] text-[13px] rounded-md px-3 py-2 outline-none focus:border-[var(--brand-primary)] transition-colors min-h-[44px] appearance-none cursor-pointer'
          >
            {statusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tabela */}
      <div className='overflow-x-auto'>
        <table className='w-full'>
          <thead>
            <tr className='border-b border-[var(--glass-border)]'>
              <th className='text-left px-5 py-3 text-[var(--font-color-label)] font-medium uppercase tracking-wider' style={{ fontSize: 'var(--font-size-label)' }}>
                Deal
              </th>
              <th className='text-left px-5 py-3 text-[var(--font-color-label)] font-medium uppercase tracking-wider' style={{ fontSize: 'var(--font-size-label)' }}>
                Empresa
              </th>
              <th className='text-left px-5 py-3 text-[var(--font-color-label)] font-medium uppercase tracking-wider' style={{ fontSize: 'var(--font-size-label)' }}>
                Beneficiário
              </th>
              <th className='text-right px-5 py-3 text-[var(--font-color-label)] font-medium uppercase tracking-wider' style={{ fontSize: 'var(--font-size-label)' }}>
                Valor Deal
              </th>
              <th className='text-right px-5 py-3 text-[var(--font-color-label)] font-medium uppercase tracking-wider' style={{ fontSize: 'var(--font-size-label)' }}>
                Comissão
              </th>
              <th className='text-center px-5 py-3 text-[var(--font-color-label)] font-medium uppercase tracking-wider' style={{ fontSize: 'var(--font-size-label)' }}>
                Status
              </th>
              <th className='text-left px-5 py-3 text-[var(--font-color-label)] font-medium uppercase tracking-wider' style={{ fontSize: 'var(--font-size-label)' }}>
                Data
              </th>
              {isAdmin && (
                <th className='text-center px-5 py-3 text-[var(--font-color-label)] font-medium uppercase tracking-wider' style={{ fontSize: 'var(--font-size-label)' }}>
                  Ações
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className='border-b border-[var(--glass-border)]'>
                  {Array.from({ length: isAdmin ? 8 : 7 }).map((_, j) => (
                    <td key={j} className='px-5 py-4'>
                      <div className='h-4 bg-[var(--glass-bg)] rounded animate-pulse' />
                    </td>
                  ))}
                </tr>
              ))
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={isAdmin ? 8 : 7} className='px-5 py-12 text-center text-[var(--text-muted)] text-sm'>
                  Nenhuma comissão encontrada
                </td>
              </tr>
            ) : (
              data.map((c) => (
                <tr key={c.id} className='border-b border-[var(--glass-border)] hover:bg-white/[0.02] transition-colors'>
                  <td className='px-5 py-3 text-[var(--text-primary)] text-[13px] font-medium'>
                    {c.deal.nome}
                  </td>
                  <td className='px-5 py-3 text-[var(--text-muted)] text-[13px]'>
                    {c.deal.empresa.nomeFantasia || c.deal.empresa.razaoSocial}
                  </td>
                  <td className='px-5 py-3 text-[var(--text-muted)] text-[13px]'>
                    {c.beneficiario.name}
                  </td>
                  <td className='px-5 py-3 text-right text-[var(--text-primary)] text-[13px] font-mono'>
                    {formatCurrency(Math.round(Number(c.deal.valor) * 100))}
                  </td>
                  <td className='px-5 py-3 text-right text-[var(--brand-primary)] text-[13px] font-mono font-semibold'>
                    {formatCurrency(c.valorCentavos)}
                  </td>
                  <td className='px-5 py-3 text-center'>
                    <Badge variant={getStatusVariant(c.status.toLowerCase())}>
                      {c.status === 'PENDENTE' ? 'Pendente' : c.status === 'PAGO' ? 'Pago' : 'Estornado'}
                    </Badge>
                  </td>
                  <td className='px-5 py-3 text-[var(--text-muted)] text-[13px]'>
                    {formatDate(c.dataPagamento || c.createdAt)}
                  </td>
                  {isAdmin && (
                    <td className='px-5 py-3 text-center'>
                      {c.status === 'PENDENTE' && (
                        <Button
                          variant='primary'
                          size='sm'
                          icon={<CheckCircle size={13} />}
                          onClick={() => handlePay(c.id)}
                        >
                          Pagar
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Paginação */}
      {meta && meta.totalPages > 1 && (
        <div className='flex items-center justify-between px-5 py-3 border-t border-[var(--glass-border)]'>
          <span className='text-[var(--text-muted)] text-xs'>
            {meta.totalItems} resultado{meta.totalItems !== 1 ? 's' : ''}
          </span>
          <div className='flex items-center gap-2'>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className='p-1.5 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-white/[0.06] disabled:opacity-30 disabled:cursor-not-allowed transition-all'
            >
              <ChevronLeft size={16} />
            </button>
            <span className='text-[var(--text-muted)] text-xs'>
              {page} / {meta.totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
              disabled={page === meta.totalPages}
              className='p-1.5 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-white/[0.06] disabled:opacity-30 disabled:cursor-not-allowed transition-all'
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
